const MAX_BYTES = 100 * 1024;

export async function compressDriverDocument(file: File): Promise<File> {
  if (file.size === 0) {
    throw new Error("Empty document cannot be uploaded.");
  }

  if (file.type === "application/pdf") {
    if (file.size > MAX_BYTES) {
      throw new Error("PDF must be 100 KB or smaller.");
    }
    return file;
  }

  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("Only JPG, PNG, WEBP or PDF documents are allowed.");
  }

  if (file.size <= MAX_BYTES) return file;

  const url = URL.createObjectURL(file);

  try {
    const image = new Image();
    image.src = url;
      try {
        await image.decode();
      } catch {
        throw new Error(
          "This image could not be opened. Please select a valid JPG, PNG or WEBP image, or a PDF under 100 KB. Convert HEIC/HEIF to JPG first."
        );
      }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Image compression is unavailable in this browser.");
    }

    const originalWidth = image.naturalWidth;
    const originalHeight = image.naturalHeight;

    if (!originalWidth || !originalHeight) {
      throw new Error("Invalid document image.");
    }

    function encode(quality: number): Promise<Blob> {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Unable to compress document image."));
          },
          "image/jpeg",
          quality
        );
      });
    }

    for (const scale of [1, 0.85, 0.7, 0.55, 0.4, 0.3, 0.2, 0.1]) {
      canvas.width = Math.max(1, Math.round(originalWidth * scale));
      canvas.height = Math.max(1, Math.round(originalHeight * scale));

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45, 0.35, 0.25]) {
        const blob = await encode(quality);

        if (blob.size > 0 && blob.size <= MAX_BYTES) {
          return new File(
            [blob],
            file.name.replace(/\\.[^.]+$/, "") + "-compressed.jpg",
            { type: "image/jpeg", lastModified: Date.now() }
          );
        }
      }
    }

    throw new Error(
      "Document could not fit within 100 KB. Please use a clearer, smaller image."
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
